/**
 * Operational Mode Store - Zustand Store
 *
 * @version 1.0
 * @module core/operational-modes
 *
 * @description
 * Store global para gestionar los modos operacionales de la plataforma.
 * Este store es TRANSVERSAL a todos los módulos (environmental, waste, mobility, etc.)
 *
 * @remarks
 * **Regla de Oro de los Paneles**:
 * - LayersPanel (izquierda) = Qué existe (capas, elementos, indicadores)
 * - BottomToolbar (abajo) = Qué estoy haciendo (herramienta activa + parámetros)
 * - ContextPanel (derecha) = Qué estoy viendo/decidiendo (detalle, diagnóstico, acciones)
 *
 * @example
 * ```tsx
 * import { useOperationalModeStore } from '@/core/operational-modes';
 *
 * function MyComponent() {
 *   const { mode, setMode, activeTool, activateTool } = useOperationalModeStore();
 *
 *   return (
 *     <div>
 *       <button onClick={() => setMode('analyze')}>Analizar</button>
 *       <p>Modo actual: {mode}</p>
 *     </div>
 *   );
 * }
 * ```
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { eventBus } from '../event-bus';
import type {
  OperationalMode,
  OperationalTool,
  LayerPreset,
  RightPanelView,
  ToolParams,
  OperationalModeState,
  OperationalModeStore,
} from './types';

/* ===== INITIAL STATE ===== */

const initialState: OperationalModeState = {
  mode: 'explore',
  activeTool: null,
  activeLayerSet: 'custom',
  selection: {
    type: 'none',
    data: null,
  },
  rightPanelView: 'summary',
  toolParams: {
    buffer: 100,
    gridSize: 100,
    snapEnabled: true,
  },
};

/* ===== STORE ===== */

/**
 * Operational Mode Store con Zustand
 *
 * @remarks
 * Gestiona el estado global de modos operacionales.
 * Emite eventos para coordinar los 3 paneles.
 *
 * Eventos emitidos:
 * - 'operational.mode.changed': Cuando cambia el modo
 * - 'operational.tool.activated': Cuando se activa una herramienta
 * - 'operational.preset.changed': Cuando se aplica un preset
 * - 'operational.selection.changed': Cuando cambia la selección
 * - 'operational.view.changed': Cuando cambia la vista del panel derecho
 */
export const useOperationalModeStore = create<OperationalModeStore>()(
  devtools(
    (set, get) => ({
      // Estado inicial
      ...initialState,

      // Acciones
      setMode: (mode: OperationalMode): void => {
        const previousMode = get().mode;

        // No hacer nada si ya está en ese modo
        if (previousMode === mode) {
          return;
        }

        // Resetear tool y selection al cambiar modo
        set({
          mode,
          activeTool: null,
          selection: { type: 'none', data: null },
        });

        // Emitir evento
        eventBus.emit('operational.mode.changed', {
          mode,
          previousMode,
          timestamp: Date.now(),
        });

        // Compatibilidad: emitir también evento legacy
        eventBus.emit('environmental.mode.changed', {
          mode,
          previousMode,
          timestamp: Date.now(),
        });

        console.debug(`[OperationalMode] Mode changed: ${previousMode} → ${mode}`);
      },

      activateTool: (tool: OperationalTool): void => {
        const previousTool = get().activeTool;
        const currentMode = get().mode;

        // No hacer nada si ya está activa
        if (previousTool === tool) {
          return;
        }

        // VALIDACIÓN DE SEGURIDAD: Bloquear herramientas incompatibles con modo actual
        const visualizeTools: OperationalTool[] = ['identify', 'measure'];

        if (currentMode === 'explore' && !visualizeTools.includes(tool)) {
          console.warn(
            `[OperationalMode] Blocked tool activation: ${tool} is not allowed in visualize mode (read-only)`
          );
          return;
        }

        // Determinar modo según herramienta (auto-switch de contexto)
        let newMode = currentMode;

        if (tool === 'drawPoint' || tool === 'drawPolygon') {
          newMode = 'inventory';
        } else if (tool === 'selectArea' || tool === 'selectRectangle') {
          newMode = 'analyze';
        } else if (tool === 'compare' || tool === 'sampleRaster') {
          newMode = 'analyze';
        } else if (tool === 'identify' || tool === 'measure') {
          newMode = 'explore';
        }

        // Actualizar estado
        set({
          activeTool: tool,
          mode: newMode,
        });

        // Emitir evento
        eventBus.emit('operational.tool.activated', {
          tool,
          previousTool,
          mode: newMode,
          timestamp: Date.now(),
        });

        // Compatibilidad: emitir también evento legacy
        eventBus.emit('environmental.tool.activated', {
          tool,
          previousTool,
          mode: newMode,
          timestamp: Date.now(),
        });

        console.debug(
          `[OperationalMode] Tool activated: ${previousTool || 'none'} → ${tool || 'none'} (mode: ${newMode})`
        );
      },

      setLayerPreset: (preset: LayerPreset): void => {
        const previousPreset = get().activeLayerSet;

        // No hacer nada si ya está activo
        if (previousPreset === preset) {
          return;
        }

        // Actualizar estado
        set({ activeLayerSet: preset });

        // Configurar herramienta y vista según preset
        switch (preset) {
          case 'heat':
          case 'green':
          case 'comfort':
          case 'risk':
            // Presets de diagnóstico → activan tool selectArea
            get().activateTool('selectArea');
            set({ rightPanelView: 'areaDiagnostics' });
            break;

          case 'interventions':
            // Preset de intervenciones → modo visualizar
            get().setMode('explore');
            set({ rightPanelView: 'summary' });
            break;

          case 'custom':
            // Custom → no cambia nada
            break;
        }

        // Emitir evento
        eventBus.emit('operational.preset.changed', {
          preset,
          previousPreset,
          timestamp: Date.now(),
        });

        // Compatibilidad: emitir también evento legacy
        eventBus.emit('environmental.preset.changed', {
          preset,
          previousPreset,
          timestamp: Date.now(),
        });

        console.debug(`[OperationalMode] Preset changed: ${previousPreset} → ${preset}`);
      },

      selectFeature: (feature: any, layerId: string): void => {
        // Actualizar selección
        set({
          selection: {
            type: 'feature',
            data: feature,
            layerId,
          },
          rightPanelView: 'featureInspector',
        });

        // Emitir evento
        eventBus.emit('operational.selection.changed', {
          type: 'feature',
          feature,
          layerId,
          timestamp: Date.now(),
        });

        // Compatibilidad: emitir también evento legacy
        eventBus.emit('environmental.selection.changed', {
          type: 'feature',
          feature,
          layerId,
          timestamp: Date.now(),
        });

        console.debug(
          `[OperationalMode] Feature selected:`,
          feature?.properties?.name || feature?.id || 'unknown',
          `(layer: ${layerId})`
        );
      },

      selectArea: (area: any): void => {
        // Actualizar selección
        set({
          selection: {
            type: 'area',
            data: area,
          },
          rightPanelView: 'areaDiagnostics',
        });

        // Emitir evento
        eventBus.emit('operational.selection.changed', {
          type: 'area',
          area,
          timestamp: Date.now(),
        });

        // Compatibilidad: emitir también evento legacy
        eventBus.emit('environmental.selection.changed', {
          type: 'area',
          area,
          timestamp: Date.now(),
        });

        console.debug(`[OperationalMode] Area selected:`, area);
      },

      clearSelection: (): void => {
        const previousSelection = get().selection;

        // No hacer nada si ya está vacía
        if (previousSelection.type === 'none') {
          return;
        }

        // Actualizar selección
        set({
          selection: {
            type: 'none',
            data: null,
          },
          rightPanelView: 'summary',
        });

        // Emitir evento
        eventBus.emit('operational.selection.changed', {
          type: 'none',
          timestamp: Date.now(),
        });

        // Compatibilidad: emitir también evento legacy
        eventBus.emit('environmental.selection.changed', {
          type: 'none',
          timestamp: Date.now(),
        });

        console.debug(`[OperationalMode] Selection cleared`);
      },

      setRightPanelView: (view: RightPanelView): void => {
        const previousView = get().rightPanelView;

        // No hacer nada si ya está en esa vista
        if (previousView === view) {
          return;
        }

        // Actualizar vista
        set({ rightPanelView: view });

        // Emitir evento
        eventBus.emit('operational.view.changed', {
          view,
          previousView,
          timestamp: Date.now(),
        });

        // Compatibilidad: emitir también evento legacy
        eventBus.emit('environmental.view.changed', {
          view,
          previousView,
          timestamp: Date.now(),
        });

        console.debug(`[OperationalMode] Panel view changed: ${previousView} → ${view}`);
      },

      updateToolParams: (params: Partial<ToolParams>): void => {
        const currentParams = get().toolParams;

        // Merge params
        const newParams = {
          ...currentParams,
          ...params,
        };

        // Actualizar
        set({ toolParams: newParams });

        console.debug(`[OperationalMode] Tool params updated:`, newParams);
      },

      reset: (): void => {
        set(initialState);
        console.debug('[OperationalMode] Reset to initial state');
      },
    }),
    {
      name: 'operational-mode-store', // Para Redux DevTools
    }
  )
);

/* ===== SELECTORS ===== */

/**
 * Selector para obtener el modo actual
 */
export const selectMode = (state: OperationalModeStore): OperationalMode => state.mode;

/**
 * Selector para obtener la herramienta activa
 */
export const selectActiveTool = (state: OperationalModeStore): OperationalTool =>
  state.activeTool;

/**
 * Selector para obtener el preset activo
 */
export const selectActiveLayerSet = (state: OperationalModeStore): LayerPreset =>
  state.activeLayerSet;

/**
 * Selector para obtener la selección actual
 */
export const selectSelection = (state: OperationalModeStore) => state.selection;

/**
 * Selector para verificar si hay selección
 */
export const selectHasSelection = (state: OperationalModeStore): boolean =>
  state.selection.type !== 'none';

/**
 * Selector para obtener la vista del panel derecho
 */
export const selectRightPanelView = (state: OperationalModeStore): RightPanelView =>
  state.rightPanelView;

/**
 * Selector para obtener los parámetros de herramientas
 */
export const selectToolParams = (state: OperationalModeStore): ToolParams => state.toolParams;

/**
 * Selector para verificar si está en modo edición
 */
export const selectIsInventory = (state: OperationalModeStore): boolean => state.mode === 'inventory';

/**
 * Selector para verificar si está en modo análisis
 */
export const selectIsAnalyzing = (state: OperationalModeStore): boolean => state.mode === 'analyze';

/**
 * Selector para verificar si está en modo planificación
 */
export const selectIsPlanning = (state: OperationalModeStore): boolean => state.mode === 'plan';

/**
 * Selector para verificar si está en modo gestión
 */
export const selectIsOperating = (state: OperationalModeStore): boolean => state.mode === 'operate';

/* ===== ALIAS PARA COMPATIBILIDAD ===== */

/**
 * @deprecated Use useOperationalModeStore instead
 */
export const useEnvironmentalUIStore = useOperationalModeStore;
